import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem, Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

export interface TaskEditValues {
  name: string;
  abschluss_start: string | null;
  abschluss_end: string | null;
  state: string;
}

export interface EditableTask extends TaskEditValues {
  id: string;
}

const TASK_STATES = ['PLANNED', 'STARTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];

/**
 * Schlanker Bearbeiten-Dialog fuer eine Gantt-Aufgabe (Name, Zeitraum, Status)
 * mit Loeschen-Aktion. Bewusst kompakt — der volle TaskFormDialog mit
 * Teilnehmern/Skills bleibt fuer die Detailpflege.
 */
export function TaskEditDialog({
  open, task, onSave, onDelete, onClose,
}: {
  open: boolean;
  task: EditableTask | null;
  onSave: (taskId: string, values: TaskEditValues) => Promise<void>;
  onDelete: (taskId: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const [form, setForm] = useState<TaskEditValues>({ name: '', abschluss_start: null, abschluss_end: null, state: 'PLANNED' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && task) {
      setForm({
        name: task.name,
        abschluss_start: task.abschluss_start,
        abschluss_end: task.abschluss_end,
        state: task.state || 'PLANNED',
      });
    }
  }, [open, task]);

  const canSave = form.name.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || busy || !task) return;
    setBusy(true);
    try { await onSave(task.id, { ...form, name: form.name.trim() }); onClose(); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (busy || !task) return;
    setBusy(true);
    try { await onDelete(task.id); onClose(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <AppDialogTitle>{t('tasks.editTitle', 'Aufgabe bearbeiten')}</AppDialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={t('tasks.name', 'Aufgabenname')} value={form.name} required fullWidth
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            type="date" label={t('arbeitspakete.startDate', 'Start')} fullWidth
            value={form.abschluss_start ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, abschluss_start: e.target.value || null }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date" label={t('arbeitspakete.endDate', 'Ende')} fullWidth
            value={form.abschluss_end ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, abschluss_end: e.target.value || null }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('fields.state', 'Status')}</InputLabel>
            <Select
              value={form.state} label={t('fields.state', 'Status')}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
            >
              {TASK_STATES.map((s) => <MenuItem key={s} value={s}>{t(`states.${s}`, s)}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={busy}>
          {tc('actions.delete', 'Löschen')}
        </Button>
        <Box>
          <Button onClick={onClose} disabled={busy}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave || busy}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
